import React, { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import { Button, Input, Label, Spinner } from "reactstrap";
import axios from "axios";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import { compararTallas } from "../../../utils/tallas";
import { abrirPDF } from "../../../utils/abrirPDF";

// Estadísticas de ventas (menú lateral, permiso menu_reportes).
// El back manda un renglón por artículo vendido con el importe ya prorrateado de la venta;
// aquí se agrupa por mes, año, departamento, artículo, cliente, vendedor, talla y día.
// Respaldo: si el .env aún no la trae (npm start viejo / deploy sin la variable) se arma con la de logs
const URL_ESTADISTICAS =
  process.env.REACT_APP_URL_ESTADISTICAS ||
  String(process.env.REACT_APP_URL_LOGS || "").replace(/logs\/?$/, "estadisticas");

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MESES_LARGOS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DIAS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

// Paleta validada (categórica: azul = periodo, naranja = año anterior) y rampa secuencial azul
const C = {
  serie1: "#2a78d6",
  serie2: "#eb6834",
  texto: "#0f172a",
  texto2: "#64748b",
  tenue: "#94a3b8",
  borde: "#e2e8f0",
  grid: "#eef2f7",
  sube: "#15803d",
  baja: "#b91c1c",
};
const RAMPA = ["#cde2fb", "#b7d3f6", "#9ec5f4", "#86b6ef", "#6da7ec", "#5598e7", "#3987e5", "#2a78d6", "#256abf", "#1c5cab", "#184f95"];

const dinero = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const dinero2 = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);
const entero = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n || 0);
const compacto = (n) =>
  Math.abs(n) >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : Math.abs(n) >= 1e3 ? `${(n / 1e3).toFixed(n >= 1e4 ? 0 : 1)}k` : entero(n);

const anioDe = (f) => String(f || "").slice(0, 4);
const mesDe = (f) => parseInt(String(f || "").slice(5, 7), 10) - 1; // 0..11
const diaDe = (f) => new Date(`${f}T12:00:00`).getDay();

function sumar(rows) {
  const ventas = new Set();
  const clientes = new Set();
  let imp = 0;
  let tot = 0;
  let cant = 0;
  rows.forEach((r) => {
    imp += r.imp;
    tot += r.tot;
    cant += r.cant;
    ventas.add(r.v);
    if (r.cli) clientes.add(r.cli);
  });
  return { imp, tot, cant, ventas: ventas.size, clientes: clientes.size };
}

// Agrupa por una llave (o varias, si la función regresa un arreglo)
function agrupar(rows, llave) {
  const m = {};
  rows.forEach((r) => {
    const ks = llave(r);
    (Array.isArray(ks) ? ks : [ks]).forEach((k) => {
      m[k] = m[k] || { k, imp: 0, cant: 0, ventas: new Set() };
      m[k].imp += r.imp;
      m[k].cant += r.cant;
      m[k].ventas.add(r.v);
    });
  });
  return Object.values(m);
}

function Delta({ actual, anterior }) {
  if (!anterior) return <span style={{ color: C.tenue, fontSize: 12 }}>sin año anterior</span>;
  const p = ((actual - anterior) / Math.abs(anterior)) * 100;
  const sube = p >= 0;
  return (
    <span style={{ color: sube ? C.sube : C.baja, fontSize: 12, fontWeight: 600 }}>
      {sube ? "▲" : "▼"} {Math.abs(p).toFixed(1)}% vs mismo periodo año anterior
    </span>
  );
}

function Tarjeta({ titulo, valor, actual, anterior, nota }) {
  return (
    <div style={{ flex: "1 1 160px", background: "#fff", border: `1px solid ${C.borde}`, borderRadius: 12, padding: "12px 16px" }}>
      <div style={{ fontSize: 11, color: C.texto2, textTransform: "uppercase", letterSpacing: 0.5 }}>{titulo}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: C.texto, lineHeight: 1.3 }}>{valor}</div>
      {nota ? <div style={{ fontSize: 12, color: C.texto2 }}>{nota}</div> : <Delta actual={actual} anterior={anterior} />}
    </div>
  );
}

function Panel({ titulo, sub, children, derecha }) {
  return (
    <div style={{ background: "#fff", border: `1px solid ${C.borde}`, borderRadius: 12, padding: 16, marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.texto }}>{titulo}</div>
          {sub ? <div style={{ fontSize: 12, color: C.texto2 }}>{sub}</div> : null}
        </div>
        {derecha}
      </div>
      <div style={{ marginTop: 12 }}>{children}</div>
    </div>
  );
}

// Barras por mes del año elegido (+ año anterior si hay). Click en un mes = filtrar ese mes.
function GraficaMeses({ actual, anterior, medida, mesSel, onMes, anio }) {
  const [hover, setHover] = useState(null);
  const W = 760;
  const H = 240;
  const pad = { l: 48, r: 8, t: 12, b: 26 };
  const hayAnt = anterior.some((v) => v > 0);
  const max = Math.max(1, ...actual, ...anterior);
  const paso = Math.pow(10, Math.floor(Math.log10(max)));
  const tope = Math.ceil(max / paso) * paso;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * tope);
  const anchoMes = (W - pad.l - pad.r) / 12;
  const y = (v) => pad.t + (H - pad.t - pad.b) * (1 - v / tope);
  const fmt = medida === "imp" ? dinero : entero;
  const barra = (x, w, v, color) => {
    const yy = y(v);
    const h = H - pad.b - yy;
    if (h <= 0) return null;
    const r = Math.min(4, w / 2, h);
    return (
      <path
        d={`M${x},${H - pad.b} V${yy + r} Q${x},${yy} ${x + r},${yy} H${x + w - r} Q${x + w},${yy} ${x + w},${yy + r} V${H - pad.b} Z`}
        fill={color}
      />
    );
  };
  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} role="img" aria-label="Ventas por mes">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke={C.grid} />
            <text x={pad.l - 6} y={y(t) + 4} fontSize="10" textAnchor="end" fill={C.texto2}>
              {medida === "imp" ? `$${compacto(t)}` : compacto(t)}
            </text>
          </g>
        ))}
        {MESES.map((m, i) => {
          const x0 = pad.l + i * anchoMes;
          const w = anchoMes * (hayAnt ? 0.36 : 0.6);
          const xa = x0 + anchoMes * (hayAnt ? 0.12 : 0.2);
          const sel = mesSel === String(i);
          const atenuar = mesSel !== "" && !sel;
          return (
            <g
              key={m}
              style={{ cursor: "pointer", opacity: atenuar ? 0.35 : 1 }}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onClick={() => onMes(sel ? "" : String(i))}
            >
              <rect x={x0} y={pad.t} width={anchoMes} height={H - pad.t - pad.b} fill={hover === i ? "rgba(42,120,214,.06)" : "transparent"} />
              {hayAnt ? barra(xa, w, anterior[i], C.serie2) : null}
              {barra(hayAnt ? xa + w + 2 : xa, w, actual[i], C.serie1)}
              <text x={x0 + anchoMes / 2} y={H - 8} fontSize="11" textAnchor="middle" fill={sel ? C.texto : C.texto2} fontWeight={sel ? 700 : 400}>
                {m}
              </text>
            </g>
          );
        })}
        <line x1={pad.l} x2={W - pad.r} y1={H - pad.b} y2={H - pad.b} stroke={C.tenue} />
      </svg>
      {hover !== null ? (
        <div
          style={{
            position: "absolute",
            top: 8,
            left: `${Math.min(78, Math.max(4, ((pad.l + (hover + 0.5) * anchoMes) / W) * 100 - 8))}%`,
            background: "#0f172a",
            color: "#fff",
            borderRadius: 8,
            padding: "6px 10px",
            fontSize: 12,
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          <div style={{ fontWeight: 700 }}>{MESES_LARGOS[hover]}</div>
          <div>
            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: C.serie1, marginRight: 6 }} />
            {anio}: {fmt(actual[hover])}
          </div>
          {hayAnt ? (
            <div>
              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: C.serie2, marginRight: 6 }} />
              {Number(anio) - 1}: {fmt(anterior[hover])}
            </div>
          ) : null}
          <div style={{ color: "#cbd5e1", marginTop: 2 }}>Click para filtrar el mes</div>
        </div>
      ) : null}
      {hayAnt ? (
        <div style={{ display: "flex", gap: 16, fontSize: 12, color: C.texto2, marginTop: 4 }}>
          <span>
            <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: C.serie1, marginRight: 6 }} />
            {anio}
          </span>
          <span>
            <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: C.serie2, marginRight: 6 }} />
            {Number(anio) - 1}
          </span>
        </div>
      ) : null}
    </div>
  );
}

// Barras horizontales con etiqueta directa (rankings)
function Ranking({ items, medida, vacio = "Sin ventas en el periodo" }) {
  if (!items.length) return <div style={{ color: C.tenue, fontSize: 13 }}>{vacio}</div>;
  const max = Math.max(1, ...items.map((i) => i[medida]));
  return (
    <div>
      {items.map((it, idx) => (
        <div key={it.k} style={{ display: "grid", gridTemplateColumns: "22px minmax(0,1fr) 92px", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <div style={{ fontSize: 12, color: C.texto2, textAlign: "right" }}>{idx + 1}</div>
          <div title={`${it.etiqueta || it.k} · ${dinero(it.imp)} · ${entero(it.cant)} pzs`}>
            <div style={{ fontSize: 12, color: C.texto, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.etiqueta || it.k}</div>
            <div style={{ height: 8, borderRadius: 4, background: C.grid }}>
              <div style={{ width: `${(it[medida] / max) * 100}%`, height: "100%", borderRadius: 4, background: C.serie1 }} />
            </div>
          </div>
          <div style={{ fontSize: 12, color: C.texto, textAlign: "right", fontWeight: 600 }}>
            {medida === "imp" ? dinero(it.imp) : `${entero(it.cant)} pzs`}
          </div>
        </div>
      ))}
    </div>
  );
}

// Columnas verticales simples (tallas, días)
function Columnas({ items, medida }) {
  const [hover, setHover] = useState(null);
  if (!items.length) return <div style={{ color: C.tenue, fontSize: 13 }}>Sin ventas en el periodo</div>;
  const max = Math.max(1, ...items.map((i) => i[medida]));
  const top = items.reduce((a, b) => (b[medida] > a[medida] ? b : a), items[0]);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 170 }}>
      {items.map((it) => {
        const h = (it[medida] / max) * 130;
        return (
          <div
            key={it.k}
            style={{ flex: 1, textAlign: "center", minWidth: 0 }}
            onMouseEnter={() => setHover(it.k)}
            onMouseLeave={() => setHover(null)}
          >
            <div style={{ fontSize: 11, color: hover === it.k || it.k === top.k ? C.texto : "transparent", fontWeight: 600, whiteSpace: "nowrap" }}>
              {medida === "imp" ? `$${compacto(it.imp)}` : entero(it.cant)}
            </div>
            <div style={{ height: Math.max(h, it[medida] > 0 ? 2 : 0), background: C.serie1, borderRadius: "4px 4px 0 0", opacity: hover && hover !== it.k ? 0.55 : 1 }} />
            <div style={{ fontSize: 11, color: C.texto2, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.k}</div>
          </div>
        );
      })}
    </div>
  );
}

function Estadisticas() {
  const { user } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [anio, setAnio] = useState("");
  const [mes, setMes] = useState(""); // "" = todo el año; "0".."11"
  const [dep, setDep] = useState("");
  const [vendedor, setVendedor] = useState("");
  const [medida, setMedida] = useState("imp"); // "imp" | "cant"
  const [vistaReporte, setVistaReporte] = useState("mes"); // "mes" | "anio"

  useEffect(() => {
    axios
      .get(URL_ESTADISTICAS + "/ventas", {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((r) => {
        const data = r.data || [];
        setRows(data);
        const anios = [...new Set(data.map((x) => anioDe(x.f)))].sort();
        setAnio(anios[anios.length - 1] || String(new Date().getFullYear()));
      })
      .catch((e) => setError((e.response && e.response.data && e.response.data.message) || "No se pudieron cargar las estadísticas"))
      .finally(() => setCargando(false));
  }, []);

  const anios = useMemo(() => [...new Set(rows.map((x) => anioDe(x.f)))].sort(), [rows]);
  const departamentos = useMemo(() => [...new Set(rows.map((x) => x.dep))].sort((a, b) => a.localeCompare(b)), [rows]);
  const vendedores = useMemo(() => [...new Set(rows.flatMap((x) => x.vend))].sort((a, b) => a.localeCompare(b)), [rows]);

  // Filtros que no dependen del tiempo (departamento, vendedor)
  const base = useMemo(
    () => rows.filter((r) => (!dep || r.dep === dep) && (!vendedor || r.vend.includes(vendedor))),
    [rows, dep, vendedor]
  );
  const enPeriodo = (r, a) => anioDe(r.f) === a && (mes === "" || mesDe(r.f) === Number(mes));
  const periodo = useMemo(() => base.filter((r) => enPeriodo(r, anio)), [base, anio, mes]); // eslint-disable-line react-hooks/exhaustive-deps
  // Año anterior al MISMO corte: si el año elegido es el actual, solo hasta el mismo día/mes de
  // hoy (si no, se compararía ene–hoy contra el año completo y siempre saldría a la baja)
  const hoyLocal = new Date();
  const limiteMD =
    Number(anio) === hoyLocal.getFullYear()
      ? `${String(hoyLocal.getMonth() + 1).padStart(2, "0")}-${String(hoyLocal.getDate()).padStart(2, "0")}`
      : "";
  const previo = useMemo(
    () => base.filter((r) => enPeriodo(r, String(Number(anio) - 1)) && (!limiteMD || String(r.f).slice(5) <= limiteMD)),
    [base, anio, mes, limiteMD] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const k = sumar(periodo);
  const kp = sumar(previo);

  const porMes = (a) => {
    const arr = Array(12).fill(0);
    base.forEach((r) => {
      if (anioDe(r.f) === a) arr[mesDe(r.f)] += r[medida];
    });
    return arr;
  };
  const serieActual = useMemo(() => porMes(anio), [base, anio, medida]); // eslint-disable-line react-hooks/exhaustive-deps
  const serieAnterior = useMemo(() => porMes(String(Number(anio) - 1)), [base, anio, medida]); // eslint-disable-line react-hooks/exhaustive-deps

  const ordenar = (arr) => arr.sort((a, b) => b[medida] - a[medida]);
  const topArticulos = useMemo(() => {
    const desc = {};
    periodo.forEach((r) => (desc[r.art] = r.desc));
    return ordenar(agrupar(periodo, (r) => r.art))
      .slice(0, 10)
      .map((x) => ({ ...x, etiqueta: `${x.k} · ${desc[x.k] || ""}` }));
  }, [periodo, medida]); // eslint-disable-line react-hooks/exhaustive-deps
  const topClientes = useMemo(() => ordenar(agrupar(periodo, (r) => r.cli || "Sin cliente")).slice(0, 10), [periodo, medida]); // eslint-disable-line react-hooks/exhaustive-deps
  const porVendedor = useMemo(() => ordenar(agrupar(periodo, (r) => r.vend)).slice(0, 10), [periodo, medida]); // eslint-disable-line react-hooks/exhaustive-deps
  const porTalla = useMemo(() => agrupar(periodo, (r) => r.tal || "S/T").sort((a, b) => compararTallas(a.k, b.k)), [periodo]);
  const porDia = useMemo(() => {
    const g = agrupar(periodo, (r) => diaDe(r.f));
    return [1, 2, 3, 4, 5, 6, 0].map((d) => {
      const x = g.find((y) => Number(y.k) === d) || { imp: 0, cant: 0 };
      return { k: DIAS[d], imp: x.imp, cant: x.cant };
    });
  }, [periodo]);

  // Dato curioso: el mejor día del periodo
  const mejorDia = useMemo(() => {
    const g = agrupar(periodo, (r) => r.f);
    if (!g.length) return null;
    return g.reduce((a, b) => (b.imp > a.imp ? b : a), g[0]);
  }, [periodo]);

  // ===== Reporte por departamento (meses del año o años) =====
  const reporte = useMemo(() => {
    const columnas = vistaReporte === "mes" ? MESES.map((m, i) => ({ id: String(i), titulo: m })) : anios.map((a) => ({ id: a, titulo: a }));
    const fuente = vistaReporte === "mes" ? base.filter((r) => anioDe(r.f) === anio) : base;
    const col = (r) => (vistaReporte === "mes" ? String(mesDe(r.f)) : anioDe(r.f));
    const filas = {};
    const totCol = {};
    fuente.forEach((r) => {
      filas[r.dep] = filas[r.dep] || { dep: r.dep, celdas: {}, cant: 0, imp: 0 };
      const f = filas[r.dep];
      const c = col(r);
      f.celdas[c] = f.celdas[c] || { cant: 0, imp: 0 };
      f.celdas[c].cant += r.cant;
      f.celdas[c].imp += r.imp;
      f.cant += r.cant;
      f.imp += r.imp;
      totCol[c] = totCol[c] || { cant: 0, imp: 0 };
      totCol[c].cant += r.cant;
      totCol[c].imp += r.imp;
    });
    const lista = Object.values(filas).sort((a, b) => b.imp - a.imp);
    const maxCelda = Math.max(1, ...lista.flatMap((f) => Object.values(f.celdas).map((c) => c[medida])));
    const total = lista.reduce((t, f) => ({ cant: t.cant + f.cant, imp: t.imp + f.imp }), { cant: 0, imp: 0 });
    return { columnas, filas: lista, totCol, maxCelda, total };
  }, [base, anio, anios, vistaReporte, medida]);

  const colorCelda = (v) => {
    if (!v) return { background: "transparent", color: C.tenue };
    const i = Math.min(RAMPA.length - 1, Math.floor((v / reporte.maxCelda) * (RAMPA.length - 1)));
    return { background: RAMPA[i], color: i >= 6 ? "#fff" : C.texto };
  };

  const tituloReporte = () =>
    `Ventas por departamento ${vistaReporte === "mes" ? `por mes ${anio}` : "por año"}${dep ? ` · ${dep}` : ""}${vendedor ? ` · ${vendedor}` : ""}`;

  function excelReporte() {
    const datos = reporte.filas.map((f) => {
      const o = { Departamento: f.dep };
      reporte.columnas.forEach((c) => {
        o[`${c.titulo} Pzs`] = (f.celdas[c.id] || {}).cant || 0;
        o[`${c.titulo} $`] = Math.round(((f.celdas[c.id] || {}).imp || 0) * 100) / 100;
      });
      o["Total Pzs"] = f.cant;
      o["Total $"] = Math.round(f.imp * 100) / 100;
      return o;
    });
    const cols = ["Departamento", ...reporte.columnas.flatMap((c) => [`${c.titulo} Pzs`, `${c.titulo} $`]), "Total Pzs", "Total $"];
    const toExcel = new ExportJsonExcel({
      fileName: `Ventas por departamento ${vistaReporte === "mes" ? anio : "por año"}`,
      datas: [{ sheetData: datos, sheetName: "Departamentos", sheetFilter: cols, sheetHeader: cols }],
    });
    toExcel.saveExcel();
  }

  function pdfReporte() {
    const doc = new jsPDF("landscape");
    doc.setFontSize(14);
    doc.text(tituloReporte(), 14, 16);
    doc.setFontSize(9);
    doc.text("Importes sin IVA. Cada celda: piezas / importe.", 14, 22);
    const celda = (c) => (c && (c.cant || c.imp) ? `${entero(c.cant)}\n${dinero(c.imp)}` : "");
    doc.autoTable({
      startY: 26,
      head: [["Departamento", ...reporte.columnas.map((c) => c.titulo), "Total"]],
      body: reporte.filas.map((f) => [f.dep, ...reporte.columnas.map((c) => celda(f.celdas[c.id])), celda(f)]),
      foot: [["Total", ...reporte.columnas.map((c) => celda(reporte.totCol[c.id])), celda(reporte.total)]],
      styles: { fontSize: 7, halign: "right" },
      columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
      showFoot: "lastPage",
    });
    abrirPDF(doc);
  }

  const nombrePeriodo = `${mes === "" ? "" : `${MESES_LARGOS[Number(mes)]} `}${anio}`;

  if (!user.menu_reportes) {
    return (
      <>
        <Header />
        <div className="container">
          <h4 style={{ marginTop: 40 }}>No tienes permiso para ver estadísticas.</h4>
        </div>
      </>
    );
  }

  const selectEstilo = { minWidth: 150 };
  const boton = (activo) => ({
    border: `1px solid ${activo ? C.serie1 : C.borde}`,
    background: activo ? C.serie1 : "#fff",
    color: activo ? "#fff" : C.texto,
    borderRadius: 999,
    padding: "4px 14px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  });

  return (
    <>
      <Header />
      <div className="card col-12" style={{ background: "transparent", border: "none", boxShadow: "none" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title">
              <i className="fas fa-chart-line" style={{ marginRight: 8, color: C.serie1 }} />
              Estadísticas
            </h3>
            <p className="page-subtitle">Ventas por mes, año, departamento, artículo, cliente y vendedor (importes sin IVA)</p>
          </div>
        </div>

        {cargando ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spinner />
          </div>
        ) : error ? (
          <h5 style={{ color: C.baja }}>{error}</h5>
        ) : (
          <>
            {/* Filtros en una fila */}
            <div className="filter-bar">
              <div>
                <Label>Año</Label>
                <Input bsSize="sm" type="select" value={anio} onChange={(e) => setAnio(e.target.value)} style={selectEstilo}>
                  {anios.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </Input>
              </div>
              <div>
                <Label>Mes</Label>
                <Input bsSize="sm" type="select" value={mes} onChange={(e) => setMes(e.target.value)} style={selectEstilo}>
                  <option value="">Todo el año</option>
                  {MESES_LARGOS.map((m, i) => (
                    <option key={m} value={String(i)}>
                      {m}
                    </option>
                  ))}
                </Input>
              </div>
              <div>
                <Label>Departamento</Label>
                <Input bsSize="sm" type="select" value={dep} onChange={(e) => setDep(e.target.value)} style={selectEstilo}>
                  <option value="">Todos</option>
                  {departamentos.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </Input>
              </div>
              <div>
                <Label>Vendedor</Label>
                <Input bsSize="sm" type="select" value={vendedor} onChange={(e) => setVendedor(e.target.value)} style={selectEstilo}>
                  <option value="">Todos</option>
                  {vendedores.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Input>
              </div>
              <div>
                <Label>Ver en</Label>
                <div style={{ display: "flex", gap: 6 }}>
                  <button type="button" style={boton(medida === "imp")} onClick={() => setMedida("imp")}>
                    $ Importe
                  </button>
                  <button type="button" style={boton(medida === "cant")} onClick={() => setMedida("cant")}>
                    Piezas
                  </button>
                </div>
              </div>
            </div>

            {/* Indicadores */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 12 }}>
              <Tarjeta titulo={`Ventas ${nombrePeriodo}`} valor={dinero(k.imp)} actual={k.imp} anterior={kp.imp} />
              <Tarjeta titulo="Piezas" valor={entero(k.cant)} actual={k.cant} anterior={kp.cant} />
              <Tarjeta titulo="Notas de venta" valor={entero(k.ventas)} actual={k.ventas} anterior={kp.ventas} />
              <Tarjeta
                titulo="Ticket promedio"
                valor={dinero(k.ventas ? k.imp / k.ventas : 0)}
                actual={k.ventas ? k.imp / k.ventas : 0}
                anterior={kp.ventas ? kp.imp / kp.ventas : 0}
              />
              <Tarjeta
                titulo="Precio prom. por pieza"
                valor={dinero2(k.cant ? k.imp / k.cant : 0)}
                actual={k.cant ? k.imp / k.cant : 0}
                anterior={kp.cant ? kp.imp / kp.cant : 0}
              />
              <Tarjeta
                titulo="Clientes que compraron"
                valor={entero(k.clientes)}
                nota={mejorDia ? `Mejor día: ${mejorDia.k.split("-").reverse().join("/")} · ${dinero(mejorDia.imp)}` : "—"}
              />
            </div>

            <Panel
              titulo={`${medida === "imp" ? "Importe" : "Piezas"} por mes · ${anio}`}
              sub={mes === "" ? "Toca un mes para ver solo ese mes en todo el tablero" : `Viendo solo ${MESES_LARGOS[Number(mes)]} · toca otra vez para quitar el filtro`}
            >
              <GraficaMeses actual={serieActual} anterior={serieAnterior} medida={medida} mesSel={mes} onMes={setMes} anio={anio} />
            </Panel>

            {/* Reporte pedido: por mes / por año, por departamento, cantidades y montos */}
            <Panel
              titulo={tituloReporte()}
              sub="Cada celda: piezas y monto (sin IVA). El color más intenso es donde más se vende."
              derecha={
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button type="button" style={boton(vistaReporte === "mes")} onClick={() => setVistaReporte("mes")}>
                    Por mes
                  </button>
                  <button type="button" style={boton(vistaReporte === "anio")} onClick={() => setVistaReporte("anio")}>
                    Por año
                  </button>
                  <Button size="sm" color="success" onClick={excelReporte}>
                    <i className="far fa-file-excel" /> Excel
                  </Button>
                  <Button size="sm" color="danger" onClick={pdfReporte}>
                    <i className="far fa-file-pdf" /> PDF
                  </Button>
                </div>
              }
            >
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "separate", borderSpacing: 2, width: "100%", fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", color: C.texto2, padding: "4px 6px" }}>Departamento</th>
                      {reporte.columnas.map((c) => (
                        <th key={c.id} style={{ textAlign: "center", color: C.texto2, padding: "4px 6px" }}>
                          {c.titulo}
                        </th>
                      ))}
                      <th style={{ textAlign: "right", color: C.texto, padding: "4px 6px" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reporte.filas.map((f) => (
                      <tr key={f.dep}>
                        <td
                          style={{ fontWeight: 600, padding: "4px 6px", whiteSpace: "nowrap", cursor: "pointer", color: dep === f.dep ? C.serie1 : C.texto }}
                          title="Click para filtrar este departamento"
                          onClick={() => setDep(dep === f.dep ? "" : f.dep)}
                        >
                          {f.dep}
                        </td>
                        {reporte.columnas.map((c) => {
                          const v = f.celdas[c.id];
                          const est = colorCelda(v ? v[medida] : 0);
                          return (
                            <td
                              key={c.id}
                              title={v ? `${f.dep} · ${c.titulo}: ${entero(v.cant)} pzs · ${dinero2(v.imp)}` : ""}
                              style={{ ...est, borderRadius: 6, padding: "4px 6px", textAlign: "right", minWidth: 74, lineHeight: 1.25 }}
                            >
                              {v ? (
                                <>
                                  <div style={{ fontWeight: 700 }}>{entero(v.cant)}</div>
                                  <div style={{ fontSize: 11 }}>{dinero(v.imp)}</div>
                                </>
                              ) : (
                                "·"
                              )}
                            </td>
                          );
                        })}
                        <td style={{ textAlign: "right", padding: "4px 6px", lineHeight: 1.25 }}>
                          <div style={{ fontWeight: 700 }}>{entero(f.cant)}</div>
                          <div style={{ fontSize: 11 }}>{dinero(f.imp)}</div>
                        </td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: `2px solid ${C.borde}` }}>
                      <td style={{ fontWeight: 700, padding: "6px" }}>Total</td>
                      {reporte.columnas.map((c) => {
                        const v = reporte.totCol[c.id];
                        return (
                          <td key={c.id} style={{ textAlign: "right", padding: "6px", lineHeight: 1.25 }}>
                            {v ? (
                              <>
                                <div style={{ fontWeight: 700 }}>{entero(v.cant)}</div>
                                <div style={{ fontSize: 11 }}>{dinero(v.imp)}</div>
                              </>
                            ) : (
                              ""
                            )}
                          </td>
                        );
                      })}
                      <td style={{ textAlign: "right", padding: "6px", lineHeight: 1.25 }}>
                        <div style={{ fontWeight: 700 }}>{entero(reporte.total.cant)}</div>
                        <div style={{ fontSize: 11, fontWeight: 700 }}>{dinero(reporte.total.imp)}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Panel>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
              <Panel titulo="Top 10 artículos" sub={nombrePeriodo}>
                <Ranking items={topArticulos} medida={medida} />
              </Panel>
              <Panel titulo="Top 10 clientes" sub={nombrePeriodo}>
                <Ranking items={topClientes} medida={medida} />
              </Panel>
              <Panel titulo="Vendedores" sub="Una venta con 2 vendedores cuenta completa para cada uno">
                <Ranking items={porVendedor} medida={medida} />
              </Panel>
              <Panel titulo="¿Qué tallas se venden?" sub={`${medida === "imp" ? "Importe" : "Piezas"} por talla · ${nombrePeriodo}`}>
                <Columnas items={porTalla} medida={medida} />
              </Panel>
              <Panel titulo="¿Qué día se vende más?" sub={`Por día de la semana · ${nombrePeriodo}`}>
                <Columnas items={porDia} medida={medida} />
              </Panel>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default Estadisticas;
